<section class="pit-card pit-card--flush">
  <div class="assets__head">
    <div>
      <h3 class="pit-title">Bens e direitos para garantia</h3>
      <p class="pit-caption">
        R$ mil · declarado a custo de aquisição
        @if (policyDefined()) {
          · garantia calculada sobre {{ lastYear() }}
        }
        @if (hasHiddenYears()) {
          ·
          <button type="button" class="pit-link" (click)="toggleYears()">
            {{
              showAllYears()
                ? 'Ver só os 2 anos mais recentes'
                : 'Ver os ' + years().length + ' anos'
            }}
          </button>
        }
      </p>
    </div>
    <div class="assets__filters" role="group" aria-label="Filtrar por situação">
      @for (item of filters; track item.key) {
        <!-- Filtro sem bem fica desabilitado: clicar levaria a uma tabela vazia. -->
        <button
          type="button"
          class="assets__filter"
          [class.assets__filter--active]="filter() === item.key"
          [attr.aria-pressed]="filter() === item.key"
          [disabled]="item.key !== 'all' && counts()[item.key] === 0"
          (click)="filter.set(item.key)">
          {{ item.label }} · {{ counts()[item.key] }}
        </button>
      }
    </div>
  </div>

  <!--
    Nome fixo à esquerda e Situação + Editar fixos à direita: a ação aparece sem rolar de lado em cada bem.
    Sem política de garantias, as colunas que dependem dela somem (o aviso fica uma vez só, no topo da aba).
  -->
  <div class="pit-table-wrap">
    <table
      class="pit-table assets__table"
      [class.assets__table--compact]="!policyDefined()"
      [class.assets__table--editable]="canEdit()">
      <thead>
        <tr>
          <th class="assets__sticky-left">Bem ou direito</th>
          @for (year of visibleYears(); track year) {
            <th class="num">31/12/{{ year }}</th>
          }
          <th class="num">Var. {{ lastYear() }}</th>
          <th class="num">Valor de referência</th>
          @if (policyDefined()) {
            <th class="num">Haircut</th>
            <th class="num">Ônus e comprom.</th>
            <th class="num">Livre p/ garantia</th>
          }
          <th class="assets__sticky-status">Situação</th>
          @if (canEdit()) {
            <th class="assets__sticky-action"><span class="sr-only">Ações</span></th>
          }
        </tr>
      </thead>
      <tbody>
        <!-- Bens agrupados pelo grupo da ficha, com subtotal por ano (da mutação patrimonial) e grupo recolhível. -->
        @for (group of groups(); track group.code) {
          <tr class="assets__group">
            <td class="assets__sticky-left assets__group-name">
              <button
                type="button"
                class="assets__group-toggle"
                [attr.aria-expanded]="group.open"
                (click)="toggleGroup(group.code)">
                <i
                  class="pi pi-chevron-right assets__chevron"
                  [class.assets__chevron--open]="group.open"
                  aria-hidden="true"></i>
                <span class="pit-strong">{{ group.label }}</span>
                <span class="pit-caption">
                  {{ group.count }} {{ group.count === 1 ? 'bem' : 'bens' }}
                </span>
              </button>
            </td>
            @for (value of group.subtotals; track $index; let last = $last) {
              <td class="num" [class.is-current]="last">{{ value }}</td>
            }
            <td
              class="num"
              [class.pit-positive]="group.change.tone === 'positive'"
              [class.pit-negative]="group.change.tone === 'negative'">
              {{ group.change.text }}
            </td>
            <td [attr.colspan]="1 + (policyDefined() ? 3 : 0)"></td>
            <td class="assets__sticky-status"></td>
            @if (canEdit()) {
              <td class="assets__sticky-action"></td>
            }
          </tr>
          @if (group.open) {
            @for (row of group.rows; track row.asset.id) {
              <tr>
                <td class="assets__name assets__sticky-left">
                  <p class="pit-strong assets__title" [title]="row.name">{{ row.name }}</p>
                  @for (note of row.securedDebts; track note) {
                    <p class="pit-caption assets__note assets__note--debt">
                      <i class="pi pi-link" aria-hidden="true"></i>
                      {{ note }}
                    </p>
                  }
                  @for (note of row.notes; track note) {
                    <p class="pit-caption assets__note">{{ note }}</p>
                  }
                </td>
                @for (value of row.values; track $index; let last = $last) {
                  <td class="num" [class.is-current]="last">{{ value }}</td>
                }
                <td
                  class="num is-current"
                  [class.pit-positive]="row.change.tone === 'positive'"
                  [class.pit-negative]="row.change.tone === 'negative'"
                  [class.pit-neutral]="row.change.tone === 'neutral'">
                  {{ row.change.text }}
                </td>
                <td class="num">
                  @if (row.counts && row.asset.referenceValue !== null) {
                    {{ thousands(row.asset.referenceValue) }}
                    @if (row.asset.referenceSource) {
                      <span class="pit-caption assets__source">{{
                        sources[row.asset.referenceSource]
                      }}</span>
                    }
                  } @else {
                    —
                  }
                </td>
                @if (policyDefined()) {
                  <td class="num">
                    {{ row.counts && row.asset.haircut !== null ? ratio(row.asset.haircut) : '—' }}
                  </td>
                  <td class="num">
                    {{ row.counts && row.encumbrance ? thousands(row.encumbrance) : '—' }}
                  </td>
                  <td class="num is-current">
                    {{ row.counts ? thousands(row.asset.freeValue) : '—' }}
                  </td>
                }
                <td class="assets__sticky-status">
                  <span [class]="'pit-tag ' + row.situation.css" [title]="row.situation.title">
                    {{ row.situation.label }}
                  </span>
                </td>
                @if (canEdit()) {
                  <td class="assets__sticky-action">
                    <button
                      type="button"
                      class="pit-link"
                      [attr.aria-label]="'Editar ' + row.name"
                      (click)="edit.emit(row.asset)">
                      Editar
                    </button>
                  </td>
                }
              </tr>
            }
          }
        } @empty {
          <tr>
            <td [attr.colspan]="columnCount()" class="assets__empty">Nenhum bem nesta situação.</td>
          </tr>
        }
      </tbody>
      <tfoot>
        <tr class="is-total">
          <!-- O total é o de todos os bens, mesmo com filtro: o rótulo diz isso. -->
          <td class="assets__sticky-left">
            {{ filter() === 'all' ? 'Total de bens e direitos' : 'Total de todos os bens' }}
          </td>
          @for (value of footerValues(); track $index) {
            <td class="num">{{ value }}</td>
          }
          <td
            class="num"
            [class.pit-positive]="totalChange().tone === 'positive'"
            [class.pit-negative]="totalChange().tone === 'negative'">
            {{ totalChange().text }}
          </td>
          <td></td>
          @if (policyDefined()) {
            <td></td>
            <td></td>
            <td class="num">{{ thousands(freeTotal()) }}</td>
          }
          <td class="assets__sticky-status"></td>
          @if (canEdit()) {
            <td class="assets__sticky-action"></td>
          }
        </tr>
      </tfoot>
    </table>
  </div>
</section>
